import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { SEO } from '../../components/common/SEO';
import {
  Award,
  Users,
  Utensils,
  Armchair,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Send,
  Copy,
  Phone,
  MessageCircle,
  ShieldCheck,
  ArrowRight,
  HelpCircle,
  Check,
  Building,
  Calendar,
  MapPin,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  UserCheck,
  QrCode,
  FileCheck,
  Lock,
  LogIn,
  Search,
  ChevronDown,
  UserPlus,
  Coffee,
  Upload,
  FileText,
  Loader2,
  ScanText,
  Info,
  RefreshCw
} from 'lucide-react';
import { Gelanggang, UserProfile, InvoiceItem, PersatuanPaymentSettings } from '../../types';
import { formatPublicName, isChildOfParent, isFamilyMember, formatEventDates, parseReceiptTimestamp } from '../../utils/formatters';
import {
  getMembersDB,
  saveInvoiceDB,
  saveTransactionDB,
  getUjianBengkungFeeForLevel,
  getNoticesDB,
  getUjianBengkungFeesDB,
  UjianBengkungFeeConfig,
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS
} from '../../services/db';
import { uploadYuranReceiptToAhliDriveJson } from '../../lib/gdrive';
import { scanReceiptFile } from '../../utils/receiptScanner';
import {
  getNextBengkungRank,
  cleanBengkungTitle,
  toDbBengkungCode,
  ASMM_BENGKUNG_RANKS,
  BengkungRankInfo
} from '../../data/bengkungRanks';

interface UjianBengkungPageProps {
  onNavigateToTab?: (tab: string, fullUrl?: string) => void;
}

interface FamilyCandidate {
  id: string;
  name: string;
  icNo: string;
  memberId?: string;
  currentBengkung?: string;
  targetBengkung: string;
  examFee: number;
}

/**
 * Resolves the rank index of a given bengkung string in ASMM_BENGKUNG_RANKS.
 * Returns -1 if empty, unrecognized, or pesilat baharu.
 */
export function getCandidateRankIndex(bengkung?: string): number {
  if (!bengkung) return -1;
  const raw = bengkung.trim().toLowerCase();
  if (raw === '' || raw === 'tiada' || raw.includes('baharu') || raw.includes('baru')) return -1;

  const dbCode = toDbBengkungCode(bengkung);
  const clean = cleanBengkungTitle(bengkung).toLowerCase().trim();

  let idx = ASMM_BENGKUNG_RANKS.findIndex((r) => r.dbCode === dbCode);
  if (idx !== -1) return idx;

  idx = ASMM_BENGKUNG_RANKS.findIndex(
    (r) =>
      r.code.toLowerCase() === clean ||
      r.bengkungName.toLowerCase() === clean ||
      clean.startsWith(r.code.toLowerCase())
  );
  return idx;
}

/**
 * Returns available target ranks for Ujian Bengkung.
 * Strictly excludes current rank and all lower ranks ("he will only upgrade and not retain").
 */
export function getAvailableTargetRanks(currentBengkung?: string): BengkungRankInfo[] {
  const currentIdx = getCandidateRankIndex(currentBengkung);
  return ASMM_BENGKUNG_RANKS.filter((r, idx) => {
    // Guru Utama is leadership title, not for examination
    if (r.code === 'Guru Utama') return false;
    // If pesilat has current bengkung, only load ranks strictly higher (idx > currentIdx)
    if (currentIdx >= 0) {
      return idx > currentIdx;
    }
    // If pesilat baharu (no bengkung), all ranks are available
    return true;
  });
}

/**
 * Formats a rank with its specific cula count and title for clear, detailed UI selection.
 * e.g. "Bengkung Hitam (Cula 1) - Tunas Pendekar Muda"
 */
export function formatBengkungDetailedWithCula(r: BengkungRankInfo): string {
  let culaDesc = '';
  if (r.module.includes('Kuning')) {
    culaDesc = r.culaCount === 0 ? 'Tanpa Cula' : `Cula ${r.culaCount} / Darjah ${r.culaCount}`;
  } else if (r.module.includes('Biru')) {
    culaDesc = 'Tanpa Cula';
  } else {
    culaDesc = r.culaCount === 0 ? 'Tanpa Cula' : `Cula ${r.culaCount}`;
  }

  const baseName = r.bengkungName.replace(/\s*\(Peringkat\s*\d+\)/i, '').replace(/\s*\(Darjah\s*\d+\)/i, '').trim();
  return `${baseName} (${culaDesc}) - ${r.rankTitle}`;
}

interface PaymentConfig {
  paymentMethod: 'fpx' | 'qr' | 'both';
  qrBankName: string;
  qrAccountName: string;
  qrAccountNo: string;
  qrNotes: string;
}

interface PricingConfig {
  freeSeatsPerPax: number;
  freeMealsPerPax: number;
  seatOriginalPrice: number;
  seatPromoPrice: number;
  mealOriginalPrice: number;
  mealPromoPrice: number;
  promoDeadline: string;
}

const DEFAULT_PRICING: PricingConfig = {
  freeSeatsPerPax: 2,
  freeMealsPerPax: 2,
  seatOriginalPrice: 37,
  seatPromoPrice: 17,
  mealOriginalPrice: 18,
  mealPromoPrice: 13,
  promoDeadline: '2026-11-30T23:59:59+08:00'
};

export const UjianBengkungPage: React.FC<UjianBengkungPageProps> = ({ onNavigateToTab }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  // Dynamic pricing configuration (customizable from Dashboard Notices page)
  const [pricingConfig, setPricingConfig] = useState<PricingConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('asmm_ujian_pricing_config');
      if (saved) {
        try {
          const p = JSON.parse(saved);
          return {
            freeSeatsPerPax: Number(p.freeSeatsPerPax) || DEFAULT_PRICING.freeSeatsPerPax,
            freeMealsPerPax: Number(p.freeMealsPerPax) || DEFAULT_PRICING.freeMealsPerPax,
            seatOriginalPrice: Number(p.seatOriginalPrice) || DEFAULT_PRICING.seatOriginalPrice,
            seatPromoPrice: Number(p.seatPromoPrice) || DEFAULT_PRICING.seatPromoPrice,
            mealOriginalPrice: Number(p.mealOriginalPrice) || DEFAULT_PRICING.mealOriginalPrice,
            mealPromoPrice: Number(p.mealPromoPrice) || DEFAULT_PRICING.mealPromoPrice,
            promoDeadline: p.promoDeadline || DEFAULT_PRICING.promoDeadline
          };
        } catch { }
      }
    }
    return DEFAULT_PRICING;
  });

  // Event details configuration (customizable from Dashboard Notices page)
  const [eventMeta, setEventMeta] = useState<{
    eventDate: string;
    eventEndDate: string;
    eventTime: string;
    eventLocation: string;
    registrationDeadline: string;
  }>({
    eventDate: '2026-12-11',
    eventEndDate: '2026-12-12',
    eventTime: '8.00 Pagi - 2.00 Petang',
    eventLocation: 'Pusat Riadah Wadi AnNour',
    registrationDeadline: '2026-12-10'
  });

  // Sync pricing config and event details from DB notice or localStorage, and listen to live updates
  useEffect(() => {
    const syncPricing = async () => {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('asmm_ujian_pricing_config');
        if (saved) {
          try {
            const p = JSON.parse(saved);
            setPricingConfig(prev => ({
              ...prev,
              freeSeatsPerPax: Number(p.freeSeatsPerPax) || prev.freeSeatsPerPax,
              freeMealsPerPax: Number(p.freeMealsPerPax) || prev.freeMealsPerPax,
              seatOriginalPrice: Number(p.seatOriginalPrice) || prev.seatOriginalPrice,
              seatPromoPrice: Number(p.seatPromoPrice) || prev.seatPromoPrice,
              mealOriginalPrice: Number(p.mealOriginalPrice) || prev.mealOriginalPrice,
              mealPromoPrice: Number(p.mealPromoPrice) || prev.mealPromoPrice,
              promoDeadline: p.promoDeadline || prev.promoDeadline
            }));

            if (p.eventDate || p.eventEndDate || p.eventLocation || p.eventTime || p.registrationDeadline) {
              setEventMeta(prev => ({
                eventDate: p.eventDate || prev.eventDate,
                eventEndDate: p.eventEndDate !== undefined ? p.eventEndDate : prev.eventEndDate,
                eventTime: p.eventTime || prev.eventTime,
                eventLocation: p.eventLocation || prev.eventLocation,
                registrationDeadline: p.registrationDeadline || prev.registrationDeadline
              }));
            }
          } catch { }
        }
      }

      try {
        const notices = await getNoticesDB();
        const ujianNotice = notices.find(n => n.hasFamilyAddon || n.title.toLowerCase().includes('ujian') || n.title.toLowerCase().includes('bengkung'));
        if (ujianNotice) {
          if (ujianNotice.hasFamilyAddon) {
            setPricingConfig({
              freeSeatsPerPax: ujianNotice.freeSeatsPerPax ?? DEFAULT_PRICING.freeSeatsPerPax,
              freeMealsPerPax: ujianNotice.freeMealsPerPax ?? DEFAULT_PRICING.freeMealsPerPax,
              seatOriginalPrice: ujianNotice.seatOriginalPrice ?? DEFAULT_PRICING.seatOriginalPrice,
              seatPromoPrice: ujianNotice.seatPromoPrice ?? DEFAULT_PRICING.seatPromoPrice,
              mealOriginalPrice: ujianNotice.mealOriginalPrice ?? DEFAULT_PRICING.mealOriginalPrice,
              mealPromoPrice: ujianNotice.mealPromoPrice ?? DEFAULT_PRICING.mealPromoPrice,
              promoDeadline: ujianNotice.promoDeadline || DEFAULT_PRICING.promoDeadline
            });
          }

          setEventMeta(prev => ({
            eventDate: ujianNotice.eventDate || prev.eventDate,
            eventEndDate: ujianNotice.eventEndDate !== undefined ? ujianNotice.eventEndDate : prev.eventEndDate,
            eventTime: ujianNotice.eventTime || prev.eventTime,
            eventLocation: ujianNotice.eventLocation || prev.eventLocation,
            registrationDeadline: ujianNotice.registrationDeadline || prev.registrationDeadline
          }));
        }
      } catch { }
    };

    syncPricing();
    window.addEventListener('asmm_db_updated', syncPricing);
    return () => window.removeEventListener('asmm_db_updated', syncPricing);
  }, []);

  // Promo deadline
  const PROMO_DEADLINE = useMemo(() => {
    try {
      const d = new Date(pricingConfig.promoDeadline);
      if (!isNaN(d.getTime())) return d;
    } catch { }
    return new Date('2026-11-30T23:59:59+08:00');
  }, [pricingConfig.promoDeadline]);

  const promoDateFormatted = useMemo(() => {
    return PROMO_DEADLINE.toLocaleDateString('ms-MY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [PROMO_DEADLINE]);

  const promoDateShort = useMemo(() => {
    return PROMO_DEADLINE.toLocaleDateString('ms-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }, [PROMO_DEADLINE]);

  // Check if promo is active
  const [isPromoActive, setIsPromoActive] = useState<boolean>(() => {
    return new Date().getTime() <= PROMO_DEADLINE.getTime();
  });

  // Real-time countdown timer
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = PROMO_DEADLINE.getTime() - now;

      if (difference > 0) {
        setIsPromoActive(true);
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setIsPromoActive(false);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [PROMO_DEADLINE]);

  // Auto-fetch details of banking and DuitNow QR from Tetapan Persatuan (/settings/payment)
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(() => {
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem('asmm_persatuan_payment_settings');
        if (raw) {
          return {
            ...DEFAULT_PERSATUAN_PAYMENT_SETTINGS,
            ...JSON.parse(raw)
          };
        }
      } catch { }
    }
    return DEFAULT_PERSATUAN_PAYMENT_SETTINGS;
  });

  // Sync with aaPanel & localStorage whenever /settings/payment changes
  useEffect(() => {
    let isMounted = true;
    const syncPersatuanPayment = async () => {
      try {
        const settings = await getPersatuanPaymentSettingsDB();
        if (isMounted && settings) {
          setPersatuanSettings(settings);
        }
      } catch (err) {
        console.warn('Gagal memuat turun tetapan akaun bank persatuan:', err);
      }
    };

    syncPersatuanPayment();
    window.addEventListener('asmm_db_updated', syncPersatuanPayment);
    window.addEventListener('storage', syncPersatuanPayment);
    return () => {
      isMounted = false;
      window.removeEventListener('asmm_db_updated', syncPersatuanPayment);
      window.removeEventListener('storage', syncPersatuanPayment);
    };
  }, []);

  // Determine available payment methods from Persatuan settings
  const isFpxEnabled = persatuanSettings.fpxEnabled;
  const isQrEnabled = persatuanSettings.qrEnabled;
  const showBothPaymentMethods = isFpxEnabled && isQrEnabled;

  // Read admin payment configuration (customizable from Dashboard Notices page, defaults to 'qr')
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('asmm_ujian_payment_config');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            paymentMethod: parsed.paymentMethod === 'fpx' ? 'fpx' : 'qr',
            qrBankName: parsed.qrBankName || 'Bank Islam Malaysia Berhad',
            qrAccountName: parsed.qrAccountName || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA',
            qrAccountNo: parsed.qrAccountNo || '02011010087654',
            qrNotes: parsed.qrNotes || 'Sila masukkan rujukan "UB2026 - [Nama Calon]" semasa pindahan DuitNow.'
          };
        } catch { }
      }
    }
    return {
      paymentMethod: 'qr',
      qrBankName: 'Bank Islam Malaysia Berhad',
      qrAccountName: 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA',
      qrAccountNo: '02011010087654',
      qrNotes: 'Sila masukkan rujukan "UB2026 - [Nama Calon]" semasa pindahan DuitNow.'
    };
  });

  // Listen to admin changes from Notices dashboard
  useEffect(() => {
    const handleConfigUpdate = () => {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('asmm_ujian_payment_config');
        if (saved) {
          try {
            setPaymentConfig(JSON.parse(saved));
          } catch { }
        }
      }
    };
    window.addEventListener('asmm_db_updated', handleConfigUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleConfigUpdate);
  }, []);

  // Active payment tab mode (defaults to 'qr')
  const [selectedPayMode, setSelectedPayMode] = useState<'fpx' | 'qr'>('qr');

  useEffect(() => {
    if (!isFpxEnabled && isQrEnabled) {
      setSelectedPayMode('qr');
    } else if (isFpxEnabled && !isQrEnabled) {
      setSelectedPayMode('fpx');
    } else if (paymentConfig.paymentMethod === 'fpx') {
      setSelectedPayMode('fpx');
    } else {
      setSelectedPayMode('qr');
    }
  }, [isFpxEnabled, isQrEnabled, paymentConfig.paymentMethod]);

  // General Family & Guardian Info
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [gelanggang, setGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [specialNotes, setSpecialNotes] = useState('');

  // Dynamic Ujian Bengkung base fees fetched from DB (e.g. settings / ujian_bengkung_fees)
  const [ujianFeesConfig, setUjianFeesConfig] = useState<UjianBengkungFeeConfig | null>(null);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        const fees = await getUjianBengkungFeesDB();
        setUjianFeesConfig(fees);
      } catch (err) {
        console.warn('Error fetching ujian bengkung fees from DB:', err);
      }
    };
    fetchFees();
    window.addEventListener('asmm_db_updated', fetchFees);
    return () => window.removeEventListener('asmm_db_updated', fetchFees);
  }, []);

  // Candidates for the whole family
  const [candidates, setCandidates] = useState<FamilyCandidate[]>([]);

  // Family Add-on quantities
  const [extraSeats, setExtraSeats] = useState<number>(0);
  const [extraMeals, setExtraMeals] = useState<number>(0);

  // Status states
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isAccountCopied, setIsAccountCopied] = useState(false);

  // User's linked family members
  const [familyMembers, setFamilyMembers] = useState<UserProfile[]>([]);
  const [availableChildren, setAvailableChildren] = useState<UserProfile[]>([]);
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentUser) {
      setParentName(currentUser.kinName || currentUser.name || '');
      setParentPhone(currentUser.kinPhone || currentUser.phone || '');
      if (currentUser.gelanggang) setGelanggang(currentUser.gelanggang);

      getMembersDB().then((allMembers) => {
        const fam = allMembers.filter((m) => {
          if (m.id === currentUser.id) return true;
          if (isFamilyMember(m, currentUser)) return true;
          return false;
        });

        setFamilyMembers(fam);
        const kids = fam.filter((m) => isChildOfParent(m, currentUser) && m.status === 'Aktif');
        setAvailableChildren(kids);

        setCandidates((prev) => {
          if (prev.length > 0) return prev;

          if (kids.length > 0) {
            return kids.map((k, idx) => {
              const cur = k.bengkung ? cleanBengkungTitle(k.bengkung) : '';
              const available = getAvailableTargetRanks(cur);
              const target = available.length > 0 ? available[0].code : (k.bengkung ? getNextBengkungRank(k.bengkung) : 'Hitam 1');
              const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);
              return {
                id: `cand-${idx + 1}-${k.id}`,
                name: k.name,
                icNo: k.icNo || '',
                memberId: k.id,
                currentBengkung: cur,
                targetBengkung: target,
                examFee: fee
              };
            });
          } else {
            const cur = currentUser.bengkung ? cleanBengkungTitle(currentUser.bengkung) : '';
            const available = getAvailableTargetRanks(cur);
            const target = available.length > 0 ? available[0].code : (currentUser.bengkung ? getNextBengkungRank(currentUser.bengkung) : 'Putih 1');
            const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);
            return [
              {
                id: 'cand-self',
                name: currentUser.name,
                icNo: currentUser.icNo || '',
                memberId: currentUser.id,
                currentBengkung: cur,
                targetBengkung: target,
                examFee: fee
              }
            ];
          }
        });
      }).catch(() => { });
    } else {
      setCandidates([]);
      setFamilyMembers([]);
      setAvailableChildren([]);
    }
  }, [currentUser]);

  // When DB fees update, automatically update all existing candidates' exam fees
  useEffect(() => {
    if (ujianFeesConfig) {
      setCandidates((prev) =>
        prev.map((c) => ({
          ...c,
          examFee: getUjianBengkungFeeForLevel(c.targetBengkung, ujianFeesConfig)
        }))
      );
    }
  }, [ujianFeesConfig]);

  // Dynamic active prices per unit
  const currentSeatPrice = isPromoActive ? pricingConfig.seatPromoPrice : pricingConfig.seatOriginalPrice;
  const currentMealPrice = isPromoActive ? pricingConfig.mealPromoPrice : pricingConfig.mealOriginalPrice;

  // Whole family calculations
  const totalCandidates = candidates.length;
  const totalExamFees = candidates.reduce((sum, c) => sum + (Number(c.examFee) || 0), 0);
  const freeFamilySeats = totalCandidates * pricingConfig.freeSeatsPerPax;
  const freeFamilyMeals = totalCandidates * pricingConfig.freeMealsPerPax;

  const totalExtraSeatCost = extraSeats * currentSeatPrice;
  const totalExtraMealCost = extraMeals * currentMealPrice;
  const totalAddOnCost = totalExtraSeatCost + totalExtraMealCost;

  // Grand Total for the Whole Family
  const grandTotalWholeFamily = totalExamFees + totalAddOnCost;

  // Total promo savings
  const originalSeatCost = extraSeats * pricingConfig.seatOriginalPrice;
  const originalMealCost = extraMeals * pricingConfig.mealOriginalPrice;
  const totalFamilySavings = isPromoActive
    ? (originalSeatCost - totalExtraSeatCost) + (originalMealCost - totalExtraMealCost)
    : 0;

  const seatDiscountPct = Math.max(0, Math.round(
    ((pricingConfig.seatOriginalPrice - pricingConfig.seatPromoPrice) / (pricingConfig.seatOriginalPrice || 1)) * 100
  ));
  const mealDiscountPct = Math.max(0, Math.round(
    ((pricingConfig.mealOriginalPrice - pricingConfig.mealPromoPrice) / (pricingConfig.mealOriginalPrice || 1)) * 100
  ));

  // Gelanggang Leaders Contact List (Synchronized with ContactPage & Official Instructors)
  const gelanggangContacts: Record<string, { leader: string; phone: string; cleanPhone: string }> = {
    'Pusaka Warisan': {
      leader: 'Jurulatih Fikriz Zaki (Ketua Gelanggang Pusaka Warisan)',
      phone: '013-581 9201',
      cleanPhone: '60135819201'
    },
    'Wadi AnNour': {
      leader: 'Jurulatih Mohd Fauzi (Ketua Gelanggang Wadi AnNour / Setiausaha Induk)',
      phone: '012-447 5157',
      cleanPhone: '60124475157'
    },
    'Semua Gelanggang': {
      leader: 'Jurulatih Mohd Fauzi (Setiausaha Induk ASMM)',
      phone: '012-447 5157',
      cleanPhone: '60124475157'
    }
  };

  const selectedContact = gelanggangContacts[gelanggang] || gelanggangContacts['Pusaka Warisan'];

  // Navigation to login and register with preserved return URL
  const handleGoToLogin = () => {
    sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
    if (onNavigateToTab) {
      onNavigateToTab('login', '/login');
    } else {
      window.history.pushState(null, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleGoToRegister = () => {
    sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
    if (onNavigateToTab) {
      onNavigateToTab('register', '/register');
    } else {
      window.history.pushState(null, '', '/register');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  // Add Candidate to Family
  const handleAddCandidate = () => {
    if (!currentUser) {
      sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
      showToast('warning', 'Log Masuk Diperlukan', 'Sila log masuk ke akaun portal anda terlebih dahulu untuk mendaftar calon pesilat.');
      handleGoToLogin();
      return;
    }

    const defaultCurrent = '';
    const available = getAvailableTargetRanks(defaultCurrent);
    const defaultTarget = available.length > 0 ? available[0].code : 'Hitam 1';
    const fee = getUjianBengkungFeeForLevel(defaultTarget, ujianFeesConfig || undefined);
    setCandidates((prev) => [
      ...prev,
      {
        id: `cand-${Date.now()}`,
        name: '',
        icNo: '',
        currentBengkung: defaultCurrent,
        targetBengkung: defaultTarget,
        examFee: fee
      }
    ]);
  };

  // Select a family member for a candidate row
  const handleSelectFamilyMemberForCandidate = (index: number, member: UserProfile) => {
    const isAlreadySelected = candidates.some(
      (c, i) => i !== index && ((c.icNo && c.icNo === member.icNo) || (c.memberId && c.memberId === member.id))
    );
    if (isAlreadySelected) {
      showToast('info', 'Calon Sudah Dipilih', `${member.name} sudah berada dalam senarai calon.`);
      return;
    }

    const cur = member.bengkung ? cleanBengkungTitle(member.bengkung) : '';
    const available = getAvailableTargetRanks(cur);
    const target = available.length > 0 ? available[0].code : (member.bengkung ? getNextBengkungRank(member.bengkung) : 'Hitam 1');
    const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);

    setCandidates((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        name: member.name,
        icNo: member.icNo || '',
        memberId: member.id,
        currentBengkung: cur,
        targetBengkung: target,
        examFee: fee
      };
      return copy;
    });

    showToast('success', 'Calon Dipilih', `${member.name} (${cur ? 'Tahap Semasa: ' + cur + ' ➔ ' : ''}Sasaran: ${target} - RM ${fee}) dipilih dari profil keluarga.`);
  };

  // Add existing linked child
  const handleAddLinkedChild = (child: UserProfile) => {
    if (!currentUser) {
      sessionStorage.setItem('asmm_intended_url', '/ujian-bengkung');
      showToast('warning', 'Log Masuk Diperlukan', 'Sila log masuk terlebih dahulu.');
      if (onNavigateToTab) onNavigateToTab('login', '/login');
      else {
        window.history.pushState(null, '', '/login');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      return;
    }

    if (candidates.some((c) => (c.icNo && c.icNo === child.icNo) || (c.memberId && c.memberId === child.id) || c.name.toLowerCase() === child.name.toLowerCase())) {
      showToast('info', 'Calon Sudah Ditambah', `${child.name} sudah berada dalam senarai calon keluarga.`);
      return;
    }

    const cur = child.bengkung ? cleanBengkungTitle(child.bengkung) : '';
    const available = getAvailableTargetRanks(cur);
    const target = available.length > 0 ? available[0].code : (child.bengkung ? getNextBengkungRank(child.bengkung) : 'Hitam 1');
    const fee = getUjianBengkungFeeForLevel(target, ujianFeesConfig || undefined);

    setCandidates((prev) => [
      ...prev.filter((c) => c.name.trim() !== ''),
      {
        id: `cand-${Date.now()}-${child.id}`,
        name: child.name,
        icNo: child.icNo || '',
        memberId: child.id,
        currentBengkung: cur,
        targetBengkung: target,
        examFee: fee
      }
    ]);
    showToast('success', 'Calon Ditambah', `${child.name} dimasukkan ke dalam senarai.`);
  };

  // Remove Candidate
  const handleRemoveCandidate = (index: number) => {
    if (candidates.length <= 1) {
      showToast('warning', 'Peringatan', 'Sekurang-kurangnya satu calon pesilat diperlukan.');
      return;
    }
    setCandidates((prev) => prev.filter((_, i) => i !== index));
  };

  // Update candidate detail
  const handleUpdateCandidate = (index: number, field: keyof FamilyCandidate, value: any) => {
    setCandidates((prev) => {
      const copy = [...prev];
      const cand = { ...copy[index], [field]: value };

      if (field === 'currentBengkung') {
        const available = getAvailableTargetRanks(value);
        const isTargetStillValid = available.some((r) => r.code === cand.targetBengkung);
        if (!isTargetStillValid) {
          cand.targetBengkung = available.length > 0 ? available[0].code : 'Peringkat Tertinggi';
        }
        cand.examFee = getUjianBengkungFeeForLevel(cand.targetBengkung, ujianFeesConfig || undefined);
      } else if (field === 'targetBengkung') {
        cand.examFee = getUjianBengkungFeeForLevel(value, ujianFeesConfig || undefined);
      }

      copy[index] = cand;
      return copy;
    });
  };

  // Copy Account Number
  const handleCopyAccountNo = async () => {
    const acc = persatuanSettings.accountNo || '02011010087654';
    const bank = persatuanSettings.bankName || 'Bank Islam Malaysia Berhad';
    try {
      await navigator.clipboard.writeText(acc);
      setIsAccountCopied(true);
      showToast('success', 'No. Akaun Disalin!', `${bank} (${acc}) sedia ditampal di aplikasi perbankan anda.`);
      setTimeout(() => setIsAccountCopied(false), 3000);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin no akaun.');
    }
  };

  const [isRefNotesCopied, setIsRefNotesCopied] = useState(false);

  // Rujukan Bayaran: combine UB2026 with Member ID digits (e.g. ASMM-011 -> UB2026011)
  const candidateRefText = useMemo(() => {
    // 1. Check primary candidate's memberId or currentUser's id
    const targetMemberId = candidates[0]?.memberId || currentUser?.id || candidates.find(c => c.memberId)?.memberId;
    if (targetMemberId) {
      const digits = targetMemberId.replace(/\D/g, '');
      if (digits) {
        // e.g. ASMM-011 -> '011', 11 -> '011', 1 -> '001'
        const paddedDigits = digits.length < 3 ? digits.padStart(3, '0') : digits.slice(-4);
        return `UB2026${paddedDigits}`;
      }
      const cleanAlpha = targetMemberId.replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
      if (cleanAlpha) {
        return `UB2026${cleanAlpha}`;
      }
    }

    // 2. Fallback to IC number digits (last 4 digits)
    const ic = candidates[0]?.icNo || currentUser?.icNo;
    if (ic) {
      const icDigits = ic.replace(/\D/g, '');
      if (icDigits.length >= 4) {
        return `UB2026${icDigits.slice(-4)}`;
      }
    }

    return 'UB2026011';
  }, [candidates, currentUser]);

  const handleCopyRefText = async () => {
    try {
      await navigator.clipboard.writeText(candidateRefText);
      setIsRefNotesCopied(true);
      showToast('success', 'Rujukan Bayaran Disalin!', `${candidateRefText} sedia ditampal di aplikasi perbankan.`);
      setTimeout(() => setIsRefNotesCopied(false), 2500);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin rujukan.');
    }
  };

  // Receipt upload & verification state (Option B: DuitNow QR)
  const [uploadedReceiptFile, setUploadedReceiptFile] = useState<File | null>(null);
  const [receiptFilePreview, setReceiptFilePreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [isReceiptSubmitted, setIsReceiptSubmitted] = useState<boolean>(false);
  const [submittedInvoice, setSubmittedInvoice] = useState<InvoiceItem | null>(null);
  const [isScanningPdf, setIsScanningPdf] = useState<boolean>(false);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [, setScanStatus] = useState<'idle' | 'success' | 'empty' | 'error'>('idle');

  const initialTimeObj = parseReceiptTimestamp('');
  const [receiptRefId, setReceiptRefId] = useState<string>('');
  const [paidDate, setPaidDate] = useState<string>(initialTimeObj.date);
  const [paidTime, setPaidTime] = useState<string>(initialTimeObj.time);

  // Sync receiptRefId with candidateRefText if not yet customized
  useEffect(() => {
    if (!receiptRefId || receiptRefId.startsWith('UB2026')) {
      setReceiptRefId(candidateRefText);
    }
  }, [candidateRefText]);

  // Handle receipt file change
  const handleReceiptFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Rule: PDF atau Imej (.pdf, .png, .jpg, .jpeg, .webp)
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp)$/i.test(file.name);
    if (!isPdf && !isImage) {
      showToast('error', 'Format Tidak Sah', 'Bukti bayaran mesti dimuat naik dalam bentuk dokumen PDF (.pdf) atau fail imej (.png, .jpg, .jpeg, .webp).');
      e.target.value = '';
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      showToast('error', 'Saiz Fail Melebihi Had (Maksimum 10MB)', `Saiz fail (${(file.size / (1024 * 1024)).toFixed(1)} MB) melebihi had maksimum 10MB.`);
      e.target.value = '';
      return;
    }

    setUploadedReceiptFile(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => {
        setReceiptFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setReceiptFilePreview(null);
    }

    setIsScanningPdf(true);
    setScanProgress('Membaca butiran resit bayaran...');
    try {
      const scanned = await scanReceiptFile(file, (msg, pct) => {
        setScanProgress(`${msg} (${pct}%)`);
      });

      if (scanned.referenceId) {
        setReceiptRefId(scanned.referenceId);
      }
      if (scanned.timestamp) {
        const parsed = parseReceiptTimestamp(scanned.timestamp);
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
      }

      if (scanned.referenceId || scanned.timestamp) {
        setScanStatus('success');
        showToast(
          'success',
          'Data Resit Dikenalpasti!',
          `No. Rujukan: ${scanned.referenceId || receiptRefId || candidateRefText} • Tarikh: ${scanned.timestamp || paidDate}`
        );
      } else {
        setScanStatus('empty');
      }
    } catch (err) {
      console.warn('[UjianBengkung] Scan receipt error:', err);
      setScanStatus('error');
    } finally {
      setIsScanningPdf(false);
    }
  };

  // Upload and submit receipt to Google Drive JSON Ahli vault and record invoice & transaction
  const handleUploadAndSubmitReceipt = async () => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila pastikan nama waris dan setiap calon pesilat telah diisi terlebih dahulu.');
      return;
    }

    if (!uploadedReceiptFile) {
      showToast('error', 'Resit Diperlukan', 'Sila pilih atau tangkap fail resit bayaran (PDF atau Imej) sebelum menghantar.');
      return;
    }

    setIsUploadingReceipt(true);
    try {
      const primaryCandidate = candidates[0];
      const targetMemberId = primaryCandidate?.memberId || currentUser?.id || `guest-${Date.now()}`;
      const targetIc = primaryCandidate?.icNo || currentUser?.icNo || '';
      const finalRefId = (receiptRefId.trim() || candidateRefText).toUpperCase();
      const today = new Date().toISOString().split('T')[0];
      const cleanTime = paidTime.length === 5 ? `${paidTime}:00` : (paidTime || '10:00:00');
      const finalPaidAt = `${paidDate || today} ${cleanTime}`;

      // Upload receipt file to Google Drive JSON Ahli vault
      const uploadRes = await uploadYuranReceiptToAhliDriveJson(
        uploadedReceiptFile,
        targetMemberId,
        parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        'Disember',
        '2026',
        targetIc
      );

      // Build invoice line items
      const invoiceItems: { id: string; description: string; quantity: number; unitPrice: number; amount: number }[] = [];
      candidates.forEach((c, idx) => {
        invoiceItems.push({
          id: `item-cand-${idx + 1}`,
          description: `Yuran Ujian ${c.targetBengkung} - ${c.name} (Termasuk ${pricingConfig.freeSeatsPerPax} Kerusi & ${pricingConfig.freeMealsPerPax} Pek Makanan Percuma)`,
          quantity: 1,
          unitPrice: Number(c.examFee) || 0,
          amount: Number(c.examFee) || 0
        });
      });
      if (extraSeats > 0) {
        invoiceItems.push({
          id: `item-extra-seats`,
          description: `Tempat Duduk Tambahan Dewan (Kadar ${isPromoActive ? 'Promosi Awalan' : 'Asal'})`,
          quantity: extraSeats,
          unitPrice: currentSeatPrice,
          amount: totalExtraSeatCost
        });
      }
      if (extraMeals > 0) {
        invoiceItems.push({
          id: `item-extra-meals`,
          description: `Pek Makanan Katering Tambahan (Kadar ${isPromoActive ? 'Promosi Awalan' : 'Asal'})`,
          quantity: extraMeals,
          unitPrice: currentMealPrice,
          amount: totalExtraMealCost
        });
      }

      const invNo = `INV-UBF-${Date.now().toString().slice(-6)}`;
      const dueDate = pricingConfig.promoDeadline ? pricingConfig.promoDeadline.split('T')[0] : '2026-11-30';

      const newInvoice: InvoiceItem = {
        id: `inv-ubf-${Date.now()}`,
        invoiceNo: invNo,
        memberId: targetMemberId,
        memberName: parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        memberPhone: parentPhone.trim() || currentUser?.phone || '',
        memberIcNo: targetIc,
        category: 'Yuran Bengkung',
        title: `Pendaftaran Ujian Bengkung & Kemudahan Keluarga (${totalCandidates} Calon)`,
        description: `Pendaftaran Ujian Bengkung sekeluarga: ${candidates.map((c) => c.name).join(', ')}. Termasuk kuota percuma ${freeFamilySeats} kerusi & ${freeFamilyMeals} pek makanan, serta ${extraSeats} kerusi tambahan & ${extraMeals} pek makanan tambahan.`,
        amount: grandTotalWholeFamily,
        status: 'Menunggu Pengesahan',
        paymentMethod: 'DuitNow QR',
        toyibBillCode: finalRefId,
        transactionRef: finalRefId,
        receiptUrl: uploadRes.fileUrl,
        receiptFileName: uploadRes.fileName,
        paidAt: finalPaidAt,
        dueDate: dueDate,
        gelanggang: gelanggang,
        createdAt: new Date().toISOString(),
        items: invoiceItems
      };

      await saveInvoiceDB(newInvoice);

      // Save income transaction
      await saveTransactionDB({
        id: `ASMM-TX-${Date.now()}`,
        date: paidDate || today,
        category: 'Yuran Bengkung',
        description: `Bayaran Ujian Bengkung & Kemudahan Keluarga: ${parentName} (${invNo}) - Ref: ${finalRefId}`,
        amount: grandTotalWholeFamily,
        type: 'Income',
        payerOrPayee: parentName.trim(),
        gelanggang: gelanggang,
        recordedBy: currentUser?.name || 'Waris Pesilat',
        status: 'Menunggu Pengesahan',
        receiptUrl: uploadRes.fileUrl,
        toyibBillCode: finalRefId
      });

      setIsReceiptSubmitted(true);
      setSubmittedInvoice(newInvoice);

      showToast(
        'success',
        'Resit Berjaya Dihantar!',
        `Fail ${uploadRes.fileName} telah disimpan ke Google Drive JSON. No. Invois: ${invNo}. Status: Menunggu Pengesahan Bendahari.`
      );
    } catch (err: any) {
      console.error('[UjianBengkung] Error uploading receipt:', err);
      showToast('error', 'Gagal Memuat Naik Resit', err?.message || 'Berlaku ralat semasa menyimpan resit bayaran.');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleSendSubmittedReceiptWhatsApp = () => {
    if (!submittedInvoice) return;
    const msg = `*PENGESAHAN BAYARAN UJIAN BENGKUNG (DUITNOW QR)*
--------------------------------------------
*No. Invois:* ${submittedInvoice.invoiceNo}
*Nama Waris:* ${submittedInvoice.memberName}
*No. Telefon:* ${submittedInvoice.memberPhone || parentPhone}
*Gelanggang:* ${submittedInvoice.gelanggang}
*Jumlah Bayaran:* RM ${submittedInvoice.amount}.00
*No. Rujukan Transaksi:* ${submittedInvoice.transactionRef || receiptRefId || candidateRefText}
*Tarikh & Masa Bayaran:* ${submittedInvoice.paidAt || `${paidDate} ${paidTime}`}
*Senarai Calon (${totalCandidates} Orang):*
${candidates.map((c, i) => `  ${i + 1}. ${c.name} (${c.targetBengkung})`).join('\n')}

*Status:* Resit telah dimuat naik ke Portal ASMM (Menunggu Pengesahan Bendahari).
${submittedInvoice.receiptUrl ? `*Pautan Resit:* ${submittedInvoice.receiptUrl}\n` : ''}
Mohon pihak pengurusan gelanggang mengesahkan pendaftaran Ujian Bengkung dan tempahan kemudahan keluarga kami. Terima kasih.`;

    const encoded = encodeURIComponent(msg);
    const waUrl = `https://wa.me/${selectedContact.cleanPhone}?text=${encoded}`;
    window.open(waUrl, '_blank');
  };

  // Generate official formatted text for WhatsApp or clipboard
  const generateFamilyConfirmationMessage = (isQrPayment = false) => {
    const candidateListText = candidates.map((c, idx) => {
      return `  ${idx + 1}. ${c.name.trim() || '[Nama Calon]'} (${c.icNo ? `IC: ${c.icNo}, ` : ''}${c.targetBengkung} - RM ${c.examFee})`;
    }).join('\n');

    return `*PENDAFTARAN UJIAN BENGKUNG & TEMPAHAN KELUARGA ASMM*
--------------------------------------------
*Maklumat Waris / Penjaga:*
• Nama Waris: ${parentName.trim() || '[Belum diisi]'}
• No. Telefon: ${parentPhone.trim() || '[Belum diisi]'}
• Gelanggang: ${gelanggang}

*Senarai Calon Pesilat Keluarga (${totalCandidates} Orang):*
${candidateListText}
• Subtotal Yuran Calon: RM ${totalExamFees}

*Kemudahan Percuma (Termasuk Pakej Calon):*
• Tempat Duduk Percuma: ${freeFamilySeats} Kerusi (${pricingConfig.freeSeatsPerPax} kerusi x ${totalCandidates} calon) = PERCUMA
• Pek Makanan Percuma: ${freeFamilyMeals} Pek (${pricingConfig.freeMealsPerPax} pek x ${totalCandidates} calon) = PERCUMA

*Tempahan Tambahan (Add-On Keluarga):*
• Kerusi Tambahan: ${extraSeats} kerusi x RM ${currentSeatPrice} = RM ${totalExtraSeatCost}
• Makanan Tambahan: ${extraMeals} pek x RM ${currentMealPrice} = RM ${totalExtraMealCost}
• Subtotal Tambahan: RM ${totalAddOnCost}

*KIRAAN JUMLAH KESELURUHAN KELUARGA:*
• Status Promosi: ${isPromoActive ? `Harga Promosi Awalan (Sebelum ${promoDateShort})` : 'Harga Asal'}
• *JUMLAH KESELURUHAN: RM ${grandTotalWholeFamily}*${isPromoActive && totalFamilySavings > 0 ? ` (Jimat RM ${totalFamilySavings}!)` : ''}
${isQrPayment ? `• Kaedah Bayaran: Pindahan DuitNow QR (${persatuanSettings.bankName || 'Bank Islam'} - ${persatuanSettings.accountNo || '02011010087654'})\n• Pemegang Akaun: ${persatuanSettings.accountHolder || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH'}\n• Rujukan Bayaran: ${candidateRefText}\n• Sila lampirkan resit slip bayaran bersama mesej ini.\n` : ''}${specialNotes.trim() ? `• Catatan Khas: ${specialNotes.trim()}\n` : ''}
Mohon pihak pengurusan gelanggang mengesahkan pendaftaran dan susun atur dewan/katering bagi keluarga kami. Terima kasih.`;
  };



  // Direct WhatsApp dispatch
  const handleSendWhatsApp = (isQrPayment = false) => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila lengkapkan nama waris dan calon pesilat terlebih dahulu.');
      return;
    }
    const msg = generateFamilyConfirmationMessage(isQrPayment);
    const encoded = encodeURIComponent(msg);
    const waUrl = `https://wa.me/${selectedContact.cleanPhone}?text=${encoded}`;
    window.open(waUrl, '_blank');
    showToast('info', 'Membuka WhatsApp', `Menghubungkan ke ${selectedContact.leader}...`);
  };

  // Online Payment / Create Invoice
  const handleCreateFamilyInvoiceAndPay = async () => {
    const invalidCandidate = candidates.find((c) => !c.name.trim());
    if (!parentName.trim() || invalidCandidate) {
      showToast('warning', 'Maklumat Belum Lengkap', 'Sila pastikan nama waris dan setiap calon pesilat telah diisi.');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const invNo = `INV-UBF-${Date.now().toString().slice(-6)}`;
      const dueDate = pricingConfig.promoDeadline ? pricingConfig.promoDeadline.split('T')[0] : '2026-11-30';

      // Build invoice line items
      const invoiceItems: { id: string; description: string; quantity: number; unitPrice: number; amount: number }[] = [];

      // 1. Line item for each candidate exam fee
      candidates.forEach((c, idx) => {
        invoiceItems.push({
          id: `item-cand-${idx + 1}`,
          description: `Yuran Ujian ${c.targetBengkung} - ${c.name} (Termasuk ${pricingConfig.freeSeatsPerPax} Kerusi & ${pricingConfig.freeMealsPerPax} Pek Makanan Percuma)`,
          quantity: 1,
          unitPrice: Number(c.examFee) || 0,
          amount: Number(c.examFee) || 0
        });
      });

      // 2. Extra seats if any
      if (extraSeats > 0) {
        invoiceItems.push({
          id: `item-extra-seats`,
          description: `Tempat Duduk Tambahan Dewan (Kadar ${isPromoActive ? 'Promosi Awalan' : 'Asal'})`,
          quantity: extraSeats,
          unitPrice: currentSeatPrice,
          amount: totalExtraSeatCost
        });
      }

      // 3. Extra meals if any
      if (extraMeals > 0) {
        invoiceItems.push({
          id: `item-extra-meals`,
          description: `Pek Makanan Katering Tambahan (Kadar ${isPromoActive ? 'Promosi Awalan' : 'Asal'})`,
          quantity: extraMeals,
          unitPrice: currentMealPrice,
          amount: totalExtraMealCost
        });
      }

      const primaryCandidate = candidates[0];
      const newInvoice: InvoiceItem = {
        id: `inv-ubf-${Date.now()}`,
        invoiceNo: invNo,
        memberId: currentUser?.id || `guest-${Date.now()}`,
        memberName: parentName.trim() || primaryCandidate.name || 'Waris Pesilat',
        memberPhone: parentPhone.trim() || currentUser?.phone || '',
        memberIcNo: primaryCandidate.icNo || '',
        category: 'Yuran Bengkung',
        title: `Pendaftaran Ujian Bengkung & Kemudahan Keluarga (${totalCandidates} Calon)`,
        description: `Pendaftaran Ujian Bengkung sekeluarga: ${candidates.map((c) => c.name).join(', ')}. Termasuk kuota percuma ${freeFamilySeats} kerusi & ${freeFamilyMeals} pek makanan, serta ${extraSeats} kerusi tambahan & ${extraMeals} pek makanan tambahan.`,
        amount: grandTotalWholeFamily,
        status: 'Belum Dibayar',
        dueDate: dueDate,
        gelanggang: gelanggang,
        createdAt: new Date().toISOString(),
        items: invoiceItems
      };

      await saveInvoiceDB(newInvoice);

      showToast(
        'success',
        'Invois Keluarga Dicipta!',
        `Invois berjaya dijana dengan jumlah RM ${grandTotalWholeFamily}. Membuka gerbang pembayaran FPX...`
      );

      // Navigate to payments page with invoice selected
      if (onNavigateToTab) {
        onNavigateToTab('payments', `/payments?inv=${newInvoice.id}`);
      } else {
        window.history.pushState(null, '', `/payments?inv=${newInvoice.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memproses invois. Sila cuba lagi atau hubungi pengurusan melalui WhatsApp.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 pb-20">
      {/* Comprehensive SEO Meta Tags Injection */}
      <SEO
        title="Pendaftaran Ujian Kenaikan Bengkung & Tempahan Kemudahan Keluarga"
        description="Pendaftaran rasmi Ujian Kenaikan Bengkung ASMM kini dibuka kepada semua pelatih yang layak. Pakej pendaftaran merangkumi kemudahan percuma untuk keluarga dan tempahan tempat duduk serta makanan tambahan."
        keywords="ujian bengkung silat, ujian kenaikan bengkung asmm, silat awang sulung merah muda, gelanggang silat kedah, yuran ujian silat, bengkung silat, kemudahan keluarga silat, fpx toyyibpay silat, duitnow qr asmm"
        ogImage="/logo-pssasmm.png"
        canonicalUrl="https://asmm.pages.dev/ujian-bengkung"
      />

      {/* Container with constrained width and generous whitespace */}
      <div className="max-w-4xl mx-auto px-3.5 sm:px-6 py-4 sm:py-10">

        {/* 1. HERO FEATURE CARD (Centerpiece with Overlapping Badge) */}
        <div className="relative pt-10 sm:pt-14 mb-8 sm:mb-10">
          {/* Overlapping Martial Rank Badge / Avatar Anchor */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-xl bg-white p-1.5 flex items-center justify-center">
              <img
                src="/logo-pssasmm.png"
                alt="PSSASMM"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Main Pure White Rounded Card */}
          <div className="bg-white rounded-3xl border border-slate-200/80 p-5 sm:p-10 pt-14 sm:pt-16 shadow-sm relative text-center">
            {/* Top Right High-Contrast Status (Centered on mobile below avatar, pinned top-right on sm+) */}
            <div className="flex justify-center sm:absolute sm:top-5 sm:right-6 mb-3 sm:mb-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-[11px] sm:text-xs font-bold border border-red-100 shadow-3xs">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                <span>Pendaftaran Rasmi Dibuka</span>
              </span>
            </div>

            {/* Official ROS Trust Tag */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[10px] sm:text-xs font-semibold mb-3 max-w-full text-center leading-tight">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>Persatuan Seni Silat Awang Sulung Merah Muda (ROS: PPM-007-02-28082022)</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight max-w-2xl mx-auto break-words">
              Pendaftaran Ujian Bengkung & Tempahan Kemudahan Keluarga
            </h1>

            {/* Official Announcement Text / Greeting */}
            <p className="text-xs sm:text-base text-slate-600 font-medium mt-2.5 sm:mt-3 max-w-xl mx-auto leading-relaxed">
              Assalamualaikum w.b.t. dan Salam Sejahtera. Pendaftaran bagi Ujian Kenaikan Bengkung kini dibuka kepada semua pelatih yang layak.
            </p>

            {/* Floating Metric Bar (Official ASMM Colors: Merah, Kuning, Biru, Hitam) */}
            <div className="bg-white rounded-2xl sm:rounded-full py-3 px-3 sm:px-8 shadow-sm border border-slate-200 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-around gap-2.5 sm:gap-6 max-w-2xl mx-auto mt-6">
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-red-600 block sm:inline">{pricingConfig.freeSeatsPerPax} Kerusi</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Percuma / Calon</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-amber-600 block sm:inline">{pricingConfig.freeMealsPerPax} Pek Makanan</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Percuma / Calon</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-blue-600 block sm:inline">Jimat RM {pricingConfig.seatOriginalPrice - pricingConfig.seatPromoPrice}</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">/ Kerusi Tambahan</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-slate-200" />
              <div className="text-center p-1">
                <span className="text-sm sm:text-lg font-black text-slate-900 block sm:inline">{promoDateShort}</span>
                <span className="text-[10px] sm:text-xs text-slate-500 font-medium sm:ml-1.5 block sm:inline">Tamat Promosi</span>
              </div>
            </div>

            {/* Event Schedule & Venue Information Bar (Editable via /notices) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5 max-w-3xl mx-auto mt-6 pt-5 border-t border-slate-100 text-left">
              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Tarikh Acara</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block">
                    {formatEventDates(eventMeta.eventDate, eventMeta.eventEndDate) || '11 & 12 Dis 2026'}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Masa Ujian</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block" title={eventMeta.eventTime}>
                    {eventMeta.eventTime}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Lokasi Majlis</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block" title={eventMeta.eventLocation}>
                    {eventMeta.eventLocation}
                  </span>
                </div>
              </div>

              <div className="p-2.5 sm:p-3 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 border border-slate-200">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Tutup Pendaftaran</span>
                  <span className="text-[11px] sm:text-xs font-black text-slate-900 truncate block">
                    {eventMeta.registrationDeadline ? new Date(eventMeta.registrationDeadline).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '15 Dis 2026'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. PROMO COUNTDOWN BANNER */}
        <div className={`mb-8 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border ${isPromoActive ? 'bg-amber-50/70 border-amber-200' : 'bg-slate-50 border-slate-200'} transition-all`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3.5">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div>
                <div className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">
                    {isPromoActive ? 'Kadar Promosi Awalan Aktif' : 'Promosi Telah Tamat'}
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold text-slate-600">
                    Sah sehingga {promoDateFormatted}
                  </span>
                </div>
                <h3 className="text-sm sm:text-lg font-black text-slate-900 mt-1">
                  {isPromoActive ? 'Nikmati Diskaun Awalan Sebelum Harga Asal Dikenakan' : 'Harga Asal Kini Berkuatkuasa'}
                </h3>
                <p className="text-xs text-slate-600 mt-0.5 max-w-md">
                  Langkah ini diambil bagi memastikan susun atur kerusi di dewan mencukupi serta urusan tempahan katering berjalan lancar dan teratur.
                </p>
              </div>
            </div>

            {/* Countdown timer pills */}
            {isPromoActive ? (
              <div className="flex items-center justify-center gap-1.5 sm:gap-2 shrink-0 flex-wrap w-full md:w-auto mt-2 md:mt-0">
                <div className="bg-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl border border-amber-200 text-center min-w-[50px] sm:min-w-[58px] shadow-xs">
                  <span className="block text-base sm:text-lg font-black text-slate-900 leading-tight">{timeLeft.days}</span>
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-500">Hari</span>
                </div>
                <span className="text-slate-400 font-bold">:</span>
                <div className="bg-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl border border-amber-200 text-center min-w-[50px] sm:min-w-[58px] shadow-xs">
                  <span className="block text-base sm:text-lg font-black text-slate-900 leading-tight">{timeLeft.hours}</span>
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-500">Jam</span>
                </div>
                <span className="text-slate-400 font-bold">:</span>
                <div className="bg-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl border border-amber-200 text-center min-w-[50px] sm:min-w-[58px] shadow-xs">
                  <span className="block text-base sm:text-lg font-black text-slate-900 leading-tight">{timeLeft.minutes}</span>
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-500">Minit</span>
                </div>
                <span className="text-slate-400 font-bold">:</span>
                <div className="bg-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl border border-amber-200 text-center min-w-[50px] sm:min-w-[58px] shadow-xs">
                  <span className="block text-base sm:text-lg font-black text-red-600 leading-tight">{timeLeft.seconds}</span>
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-slate-500">Saat</span>
                </div>
              </div>
            ) : (
              <div className="px-4 py-2 bg-slate-200 text-slate-700 text-xs font-bold rounded-xl">
                Harga Penuh Berkuatkuasa
              </div>
            )}
          </div>
        </div>

        {/* 3. SECTION: MAKLUMAT PAKEJ PENDAFTARAN CALON */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-red-600" />
                <span>Maklumat Pakej Pendaftaran Calon</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Setiap bayaran yuran pendaftaran bengkung telah merangkumi kemudahan percuma untuk ahli keluarga seperti berikut:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Free Seats Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs hover:border-red-200 transition flex items-start gap-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0 text-red-600">
                <Armchair className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-extrabold text-slate-900">{pricingConfig.freeSeatsPerPax} Tempat Duduk</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-wider">
                    Percuma
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {pricingConfig.freeSeatsPerPax} kerusi bernombor percuma untuk ibu bapa atau waris menyaksikan sesi ujian di dalam dewan.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {totalCandidates > 1
                      ? `Keluarga dengan ${totalCandidates} calon automatik dapat ${freeFamilySeats} kerusi percuma!`
                      : `1 calon automatik dapat ${pricingConfig.freeSeatsPerPax} kerusi percuma!`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Free Meals Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs hover:border-amber-200 transition flex items-start gap-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0 text-amber-600">
                <Utensils className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-extrabold text-slate-900">{pricingConfig.freeMealsPerPax} Pek Makanan</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider">
                    Percuma
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {pricingConfig.freeMealsPerPax} hidangan pek katering lengkap (makanan dan minuman berbungkus) disediakan untuk ahli keluarga.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {totalCandidates > 1
                      ? `Keluarga dengan ${totalCandidates} calon automatik dapat ${freeFamilyMeals} pek makanan percuma!`
                      : `1 calon automatik dapat ${pricingConfig.freeMealsPerPax} pek makanan percuma!`
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. SECTION: SUSUNAN TENTATIF PROGRAM & PERINGATAN SARAPAN */}
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-600" />
                <span>Susunan Tentatif Program</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Jadual rasmi perjalanan Ujian Kenaikan Bengkung & Majlis Penganugerahan Sandang.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <span>Sesi Pagi & Petang</span>
              </span>
            </div>
          </div>

          {/* Notice: Sarapan Siap-siap Sebelum Hadir */}
          <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-amber-50/80 border-2 border-amber-300 shadow-xs flex items-start gap-3.5">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Coffee className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-amber-200/90 text-amber-900 text-[10px] sm:text-[11px] font-black uppercase tracking-wider">
                  Peringatan Kehadiran
                </span>
                <span className="text-xs font-bold text-amber-900 hidden sm:inline">• Pendaftaran 7:30 Pagi</span>
              </div>
              <h4 className="text-sm sm:text-base font-extrabold text-slate-900 mt-1">
                Diminta Peserta Makan Sarapan Siap-Siap Sebelum Hadir
              </h4>
              <p className="text-xs sm:text-sm text-slate-700 mt-1 leading-relaxed">
                Semua peserta dan calon pesilat <strong>diminta makan sarapan siap-siap sebelum hadir</strong> ke dewan/gelanggang pada jam 7:30 pagi agar kekal cergas dan bersedia untuk adat penerimaan serta ujian fizikal. Pek hidangan makanan katering tengah hari yang lengkap akan diagihkan sebelum waktu rehat tengah hari.
              </p>
            </div>
          </div>

          {/* Single Column Timeline Container */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-7 shadow-xs">
            {/* --- SESI PAGI --- */}
            <div className="mb-8">
              {/* Sesi Pagi Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2">
                <div>
                  <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block">Bahagian 1</span>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    SESI PAGI: Adat Penerimaan & Ujian Fizikal
                  </h3>
                </div>
                <span className="self-start sm:self-auto px-3 py-1 bg-amber-50 text-amber-800 text-xs font-bold rounded-full border border-amber-200">
                  07:30 AM – 02:00 PM
                </span>
              </div>

              {/* Sesi Pagi Timeline Items */}
              <div className="mt-5 space-y-5">
                {/* 07:30 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      07:30 AM – 08:00 AM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">Pendaftaran Peserta</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Pendaftaran calon pesilat, semakan nombor tempat duduk & agihan kupon makanan.
                    </p>
                  </div>
                </div>

                {/* 08:00 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-amber-900 bg-amber-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      08:00 AM – 09:30 AM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Upacara Penerimaan Anak Murid (Mandi Limau)
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Istiadat Buka Gelanggang</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Sesi Mandi Limau <strong className="text-slate-900 font-bold">(5 Calon Baru)</strong></span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                        <span>Salam Guru, Akad Penerimaan & Bacaan Ikrar</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 09:30 AM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-red-900 bg-red-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      09:30 AM – 12:45 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Ujian Kenaikan Bengkung <span className="text-red-600 font-bold">(15 Calon)</span>
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span><strong>Kumpulan 1:</strong> Tahap 1 (1 calon) & Tahap 2 (8 calon)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span><strong>Kumpulan 2:</strong> Tahap 3 (6 calon)</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 12:45 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-blue-900 bg-blue-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      12:45 PM – 02:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-extrabold text-slate-900">
                        REHAT - Makan Tengah Hari & Solat Zohor Berjemaah
                      </h4>
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-wider">
                        Katering
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Pengagihan pek hidangan makan tengah hari katering berbungkus kepada waris & pesilat, serta Solat Zohor berjemaah.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* --- SESI PETANG --- */}
            <div className="pt-6 border-t border-slate-200/80">
              {/* Sesi Petang Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2">
                <div>
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider block">Bahagian 2</span>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    SESI PETANG: Pengisian Minda & Majlis Kemuncak (Grand Finale)
                  </h3>
                </div>
                <span className="self-start sm:self-auto px-3 py-1 bg-red-50 text-red-800 text-xs font-bold rounded-full border border-red-200 whitespace-nowrap">
                  02:00 PM – 05:30 PM
                </span>
              </div>

              {/* Sesi Petang Timeline Items */}
              <div className="mt-5 space-y-5">
                {/* 02:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      02:00 PM – 03:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Sharing Talk & Amanat Barisan Jurulatih
                    </h4>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0"></span>
                        <span>Perkongsian ilmu, sejarah & motivasi persilatan</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0"></span>
                        <span>Urusetia: Kira markah & susun <strong className="text-slate-900 font-bold">32 sandang & sijil</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 03:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5 pb-4 border-b border-slate-100">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-red-900 bg-red-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      03:00 PM – 05:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-black text-slate-900">
                        MAJLIS KEMUNCAK & PENGANUGERAHAN
                      </h4>
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-wider">
                        Kemuncak
                      </span>
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Ucapan Perasmian Penutup</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Pengumuman Keputusan & Pemakaian Bengkung (Tahap 1, 2, 3)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0"></span>
                        <span>Penganugerahan Sandang</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 05:00 PM */}
                <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-5">
                  <div className="w-auto sm:w-48 shrink-0">
                    <span className="inline-block text-xs font-mono font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                      05:00 PM – 05:30 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Sesi Bergambar, Tutup Gelanggang & Bersurai
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Sesi fotografi kenangan bersama guru, jurulatih dan pesilat serta istiadat penutupan gelanggang rasmi.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. SECTION: FORM PENDAFTARAN CALON-CALON SEKELUARGA */}
        <div className="mb-10 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="border-b border-slate-100 pb-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-red-600" />
                <span>Maklumat Calon Pesilat Sekeluarga</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Daftarkan seorang atau lebih calon pesilat dalam keluarga anda. Setiap calon menyumbang {pricingConfig.freeSeatsPerPax} kerusi & {pricingConfig.freeMealsPerPax} pek makanan percuma.
              </p>
            </div>
            {currentUser && (
              <button
                type="button"
                onClick={handleAddCandidate}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition shadow-xs cursor-pointer self-start sm:self-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Tambah Calon</span>
              </button>
            )}
          </div>

          {!currentUser ? (
            /* Login Gate: User MUST log in to register candidates */
            <div className="p-6 sm:p-10 rounded-3xl border-2 border-dashed border-red-200 bg-red-50/30 text-center max-w-2xl mx-auto my-4">
              <div className="w-16 h-16 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4 shadow-xs">
                <Lock className="w-8 h-8" />
              </div>
              <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-[11px] font-black uppercase tracking-wider rounded-full mb-2">
                Log Masuk Diperlukan
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-2">
                Sila Log Masuk untuk Mendaftar Calon Pesilat
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-lg mx-auto mb-6">
                Untuk mendaftarkan nama calon pesilat dan membuat tempahan pakej keluarga, anda perlu log masuk ke akaun portal ASMM. Sistem akan memautkan maklumat anak/keluarga serta mencadangkan tahap bengkung dan yuran yang tepat secara automatik.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={handleGoToLogin}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-md hover:shadow-lg transition cursor-pointer"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Log Masuk Akaun Portal</span>
                </button>
                <button
                  type="button"
                  onClick={handleGoToRegister}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 text-xs sm:text-sm font-bold rounded-2xl shadow-xs transition cursor-pointer"
                >
                  <UserPlus className="w-4 h-4 text-slate-600" />
                  <span>Daftar Akaun Baharu</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-red-100 text-left">
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">1. Carian Keluarga Mudah</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Pilih nama anak terus daripada profil keluarga tanpa perlu mengisi semula nombor MyKid.
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">2. Yuran DB Dinamik</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Kadar yuran dikira secara automatik mengikut penetapan terkini pangkalan data ASMM.
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-red-100/80 shadow-2xs">
                  <span className="block text-xs font-bold text-slate-900">3. Invois Rasmi ASMM</span>
                  <span className="text-[11px] text-slate-500 leading-tight block mt-0.5">
                    Invois dan resit rasmi berdaftar ROS dijana serta-merta untuk simpanan peribadi waris.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* Logged in view with Family Member Search & Dynamic Bengkung Fees */
            <div>
              {/* Quick Add from Linked Family Members */}
              {familyMembers.length > 0 && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2.5">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-red-600" />
                      <span>Pilih Pantas Dari Senarai Keluarga Anda ({familyMembers.length} Ahli):</span>
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Klik nama untuk tambah ke senarai calon dengan yuran automatik
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {familyMembers.map((member) => {
                      const isAlreadyAdded = candidates.some(
                        (c) =>
                          (c.icNo && c.icNo === member.icNo) ||
                          (c.memberId && c.memberId === member.id) ||
                          (c.name && c.name.trim().toLowerCase() === member.name.trim().toLowerCase())
                      );
                      return (
                        <button
                          key={member.id}
                          type="button"
                          disabled={isAlreadyAdded}
                          onClick={() => handleAddLinkedChild(member)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${isAlreadyAdded
                            ? 'bg-slate-200 text-slate-400 border border-slate-200 cursor-not-allowed'
                            : 'bg-white hover:bg-red-50 hover:border-red-200 border border-slate-200 text-slate-800 shadow-2xs'
                            }`}
                        >
                          <UserCheck className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-slate-400' : 'text-red-600'}`} />
                          <span>{member.name}</span>
                          <span className="text-[10px] font-normal text-slate-500">
                            ({member.bengkung ? cleanBengkungTitle(member.bengkung) : 'Pesilat'})
                          </span>
                          {isAlreadyAdded && (
                            <span className="text-[10px] text-emerald-600 font-bold ml-1">✓ Dipilih</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Guardian Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4 bg-slate-50/70 rounded-2xl border border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Nama Ibu Bapa / Waris <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Ahmad bin Ismail"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    No. WhatsApp / Telefon Waris <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="Contoh: 012-3456789"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Gelanggang Latihan <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={gelanggang}
                    onChange={(e) => setGelanggang(e.target.value as Gelanggang)}
                    className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none"
                  >
                    <option value="Pusaka Warisan">Gelanggang Pusat Pusaka Warisan</option>
                    <option value="Wadi AnNour">Gelanggang Al-Wadi An-Nour</option>
                  </select>
                </div>
              </div>

              {/* List of Candidates */}
              <div className="space-y-4">
                {candidates.map((cand, idx) => {
                  const availableRanks = getAvailableTargetRanks(cand.currentBengkung);

                  return (
                    <div
                      key={cand.id}
                      className="p-5 sm:p-6 rounded-3xl border border-slate-200 bg-white hover:border-slate-300 shadow-xs hover:shadow-sm transition-all relative space-y-5"
                    >
                      {/* Top Header Row of Candidate Card */}
                      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-lg bg-slate-900 text-white text-xs font-black flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                            Calon {idx + 1}: {cand.name ? cand.name : 'Pesilat Baharu'}
                          </h3>
                        </div>

                        {candidates.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCandidate(idx)}
                            className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition cursor-pointer"
                            title="Padam Calon Ini"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Main Form Fields: 2 Spacious Columns */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        {/* Lajur 1: Nama Penuh Calon (Unified combobox) */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-bold text-slate-700">
                              Nama Calon Pesilat <span className="text-red-600">*</span>
                            </label>
                            {familyMembers.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setActiveSearchIndex(activeSearchIndex === idx ? null : idx)}
                                className="text-[11px] text-red-600 hover:text-red-700 font-bold flex items-center gap-1 cursor-pointer"
                              >
                                <Search className="w-3 h-3" />
                                <span>Pilih Profil Keluarga ({familyMembers.length})</span>
                              </button>
                            )}
                          </div>

                          <div className="relative">
                            <input
                              type="text"
                              placeholder={familyMembers.length > 0 ? "Taip nama calon atau pilih dari profil keluarga..." : "Taip nama penuh calon pesilat..."}
                              value={cand.name}
                              onFocus={() => {
                                if (familyMembers.length > 0 && !cand.name) {
                                  setActiveSearchIndex(idx);
                                }
                              }}
                              onChange={(e) => {
                                const val = e.target.value;
                                handleUpdateCandidate(idx, 'name', val);
                                if (val.trim()) {
                                  setActiveSearchIndex(idx);
                                }
                                if (cand.memberId) {
                                  const matched = familyMembers.find((m) => m.id === cand.memberId);
                                  if (matched && matched.name !== val) {
                                    handleUpdateCandidate(idx, 'memberId', undefined);
                                  }
                                }
                              }}
                              className="w-full pl-3.5 pr-9 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-medium shadow-2xs"
                            />

                            {familyMembers.length > 0 && (
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setActiveSearchIndex(activeSearchIndex === idx ? null : idx)}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-md cursor-pointer"
                                title="Buka senarai profil keluarga"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeSearchIndex === idx ? 'rotate-180 text-red-600' : ''}`} />
                              </button>
                            )}

                            {/* Floating Popover for Family Members */}
                            {activeSearchIndex === idx && familyMembers.length > 0 && (
                              <>
                                <div
                                  className="fixed inset-0 z-20"
                                  onClick={() => setActiveSearchIndex(null)}
                                />
                                <div className="absolute z-30 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 space-y-1 max-h-60 overflow-y-auto">
                                  <div className="text-[11px] font-bold text-slate-500 px-2 py-1 flex items-center justify-between border-b border-slate-100">
                                    <span>Pilih Ahli Keluarga ASMM:</span>
                                    <button
                                      type="button"
                                      onClick={() => setActiveSearchIndex(null)}
                                      className="text-slate-400 hover:text-slate-600 text-xs cursor-pointer font-bold"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  {familyMembers
                                    .filter((m) => !cand.name || m.name.toLowerCase().includes(cand.name.toLowerCase().trim()))
                                    .map((m) => {
                                      const isAlreadySelected = candidates.some((c, i) => i !== idx && c.memberId === m.id);
                                      return (
                                        <button
                                          key={m.id}
                                          type="button"
                                          disabled={isAlreadySelected}
                                          onClick={() => {
                                            handleSelectFamilyMemberForCandidate(idx, m);
                                            setActiveSearchIndex(null);
                                          }}
                                          className={`w-full text-left p-2 rounded-xl text-xs flex items-center justify-between transition ${isAlreadySelected
                                            ? 'opacity-40 bg-slate-50 cursor-not-allowed'
                                            : 'hover:bg-red-50 hover:text-red-700 cursor-pointer'
                                            }`}
                                        >
                                          <div>
                                            <span className="font-bold text-slate-800 block">{m.name}</span>
                                            <span className="text-[10px] text-slate-500">
                                              Bengkung: {m.bengkung ? cleanBengkungTitle(m.bengkung) : 'Pesilat Asas'} • IC: {m.icNo || 'Tiada'}
                                            </span>
                                          </div>
                                          <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${isAlreadySelected
                                            ? 'bg-slate-200 text-slate-500'
                                            : 'bg-red-50 text-red-600 border border-red-200'
                                            }`}>
                                            {isAlreadySelected ? 'Dipilih' : 'Pilih'}
                                          </span>
                                        </button>
                                      );
                                    })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Lajur 2: No MyKid / Kad Pengenalan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            No. MyKid / Kad Pengenalan
                          </label>
                          <input
                            type="text"
                            placeholder="Contoh: 120512-02-1234"
                            value={cand.icNo}
                            onChange={(e) => handleUpdateCandidate(idx, 'icNo', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-medium shadow-2xs"
                          />
                        </div>

                        {/* Lajur 3: Tahap Bengkung Semasa */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            Tahap Bengkung Semasa
                          </label>
                          <select
                            value={cand.currentBengkung || ''}
                            onChange={(e) => handleUpdateCandidate(idx, 'currentBengkung', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-slate-50 font-medium text-slate-800 shadow-2xs"
                          >
                            <option value="">Pesilat Baharu (Belum Berbengkung)</option>
                            {ASMM_BENGKUNG_RANKS.filter((r) => r.code !== 'Guru Utama').map((r) => (
                              <option key={r.code} value={r.code}>
                                {formatBengkungDetailedWithCula(r)}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Lajur 4: Tahap Bengkung Sasaran Ujian */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700">
                            Tahap Bengkung Sasaran Ujian <span className="text-red-600">*</span>
                          </label>
                          <select
                            value={cand.targetBengkung}
                            onChange={(e) => handleUpdateCandidate(idx, 'targetBengkung', e.target.value)}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:ring-2 focus:ring-red-500 focus:outline-none bg-white font-semibold text-slate-900 shadow-2xs"
                          >
                            {availableRanks.length === 0 ? (
                              <option value="" disabled>
                                Peringkat Tertinggi (Tiada Ujian Lanjutan)
                              </option>
                            ) : (
                              availableRanks.map((r) => (
                                <option key={r.code} value={r.code}>
                                  {formatBengkungDetailedWithCula(r)} (RM {getUjianBengkungFeeForLevel(r.code, ujianFeesConfig || undefined)})
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      {/* Card Footer Bar */}
                      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 font-semibold text-emerald-700">
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                          <span>Termasuk {pricingConfig.freeSeatsPerPax} Kerusi & {pricingConfig.freeMealsPerPax} Pek Makanan Percuma untuk waris</span>
                        </span>
                        <span className="font-extrabold text-slate-900 sm:text-right">
                          Yuran Calon: <strong className="text-red-600">RM {cand.examFee}.00</strong>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddCandidate}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition cursor-pointer"
                >
                  <Plus className="w-4 h-4 text-slate-600" />
                  <span>+ Tambah Calon Seterusnya</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 5. SECTION: TEMPAHAN TAMBAHAN (ADD-ON KELUARGA) */}
        <div className="mb-10">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">

              <span>Tempahan Tambahan (Add-On Melebihi Kuota Percuma)</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Bagi ibu bapa atau waris yang ingin membawa ahli keluarga melebihi kuota percuma yang disediakan, tempahan tempat duduk dan makanan tambahan hendaklah dibuat mengikut ketetapan berikut:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            {/* Add-on Seats Counter */}
            <div className="bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-blue-400 transition-all shadow-xs relative flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                      <Armchair className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Tempat Duduk Tambahan</h3>
                      <span className="text-xs text-slate-500">Kerusi bernombor dewan</span>
                    </div>
                  </div>
                  {isPromoActive && (
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-extrabold rounded-full">
                      Jimat {seatDiscountPct}%
                    </span>
                  )}
                </div>

                <div className="my-5 p-3.5 bg-slate-50 rounded-2xl flex items-baseline justify-between">
                  <div>
                    <span className="text-xs text-slate-500 block">Kadar Setiap Kerusi:</span>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="text-2xl font-black text-slate-900">
                        RM {currentSeatPrice}
                      </span>
                      {isPromoActive && (
                        <span className="text-sm font-semibold text-slate-400 line-through">
                          RM {pricingConfig.seatOriginalPrice}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-slate-500">
                    {isPromoActive ? 'Harga Promosi Awalan' : 'Harga Asal'}
                  </span>
                </div>
              </div>

              {/* Counter Buttons */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Kerusi Tambahan:</span>
                  <span className="text-[11px] text-slate-500">
                    Selain {totalCandidates > 0 ? `${freeFamilySeats} kerusi percuma` : `kuota percuma (${pricingConfig.freeSeatsPerPax} kerusi/calon)`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExtraSeats(Math.max(0, extraSeats - 1))}
                    disabled={extraSeats === 0}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-white flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-lg font-black text-slate-900">
                    {extraSeats}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExtraSeats(extraSeats + 1)}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Add-on Meals Counter */}
            <div className="bg-white rounded-3xl p-6 border-2 border-slate-200 hover:border-amber-400 transition-all shadow-xs relative flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                      <Utensils className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Makanan Tambahan</h3>
                      <span className="text-xs text-slate-500">Pek katering berbungkus</span>
                    </div>
                  </div>
                  {isPromoActive && (
                    <span className="px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-extrabold rounded-full">
                      Jimat {mealDiscountPct}%
                    </span>
                  )}
                </div>

                <div className="my-5 p-3.5 bg-slate-50 rounded-2xl flex items-baseline justify-between">
                  <div>
                    <span className="text-xs text-slate-500 block">Kadar Setiap Pax:</span>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="text-2xl font-black text-slate-900">
                        RM {currentMealPrice}
                      </span>
                      {isPromoActive && (
                        <span className="text-sm font-semibold text-slate-400 line-through">
                          RM {pricingConfig.mealOriginalPrice}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-slate-500">
                    {isPromoActive ? 'Harga Promosi Awalan' : 'Harga Asal'}
                  </span>
                </div>
              </div>

              {/* Counter Buttons */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Pek Makanan Tambahan:</span>
                  <span className="text-[11px] text-slate-500">
                    Selain {totalCandidates > 0 ? `${freeFamilyMeals} pek percuma` : `kuota percuma (${pricingConfig.freeMealsPerPax} pek/calon)`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExtraMeals(Math.max(0, extraMeals - 1))}
                    disabled={extraMeals === 0}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-white flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-lg font-black text-slate-900">
                    {extraMeals}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExtraMeals(extraMeals + 1)}
                    className="w-9 h-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-800 transition shadow-xs cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 6. NOTICE BOX: PENTING TARIKH PROMOSI */}
        <div className="mb-10 p-5 sm:p-6 rounded-3xl bg-red-50 border-2 border-red-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-red-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-black text-red-950 uppercase tracking-tight">
                PENTING: Syarat Kadar Diskaun & Susun Atur Dewan
              </h3>
              <p className="text-xs sm:text-sm text-red-900 leading-relaxed font-semibold">
                Kadar diskaun dan harga promosi awalan ini hanya sah untuk pembelian dan pengesahan yang dibuat sebelum atau pada {promoDateFormatted} sahaja. Selepas tarikh tersebut, semua pembelian tambahan akan dikenakan pada harga asal (Kerusi: RM {pricingConfig.seatOriginalPrice} / Makanan: RM {pricingConfig.mealOriginalPrice}).
              </p>
              <p className="text-xs text-red-800 leading-relaxed">
                Langkah ini diambil bagi memastikan susun atur kerusi di dewan mencukupi serta urusan tempahan katering berjalan lancar dan teratur. Sila buat pendaftaran dan pengesahan kehadiran ahli keluarga tambahan kepada pihak pengurusan gelanggang dengan kadar segera.
              </p>
            </div>
          </div>
        </div>

        {/* 7. COMPLETE FAMILY BREAKDOWN & PAYMENT SUMMARY (PECAHAN PENUH KELUARGA) */}
        <div className="mb-10 bg-white rounded-3xl border-2 border-slate-900 p-6 sm:p-8 shadow-xl">
          <div className="border-b border-slate-200 pb-4 mb-6 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider block">
                Penyata & Ringkasan Bayaran
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-0.5">
                Pecahan Keseluruhan Keluarga
              </h2>
            </div>
            <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-xs font-bold">
              {totalCandidates > 0 ? `${totalCandidates} Calon Terlibat` : 'Keluarga ASMM'}
            </span>
          </div>

          {/* Itemized Table Breakdown */}
          <div className="space-y-3 mb-6">
            {/* Candidates exam fee rows */}
            {candidates.map((c, i) => (
              <div key={c.id} className="flex justify-between items-center py-2 border-b border-slate-100 text-xs sm:text-sm gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="font-bold text-slate-900 truncate block">{c.name || `Calon ${i + 1}`}</span>
                    <span className="text-slate-500 text-[11px] sm:text-xs font-medium">({c.targetBengkung})</span>
                  </div>
                </div>
                <span className="font-mono font-bold text-slate-900 shrink-0">RM {c.examFee}.00</span>
              </div>
            ))}

            {/* Free Family Package (0 RM) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between py-2.5 px-3.5 bg-emerald-50 rounded-xl border border-emerald-200 text-xs sm:text-sm text-emerald-900 font-semibold gap-1.5 sm:gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="leading-snug">
                  {totalCandidates > 0
                    ? `Pakej Percuma Ahli Keluarga (${freeFamilySeats} Kerusi + ${freeFamilyMeals} Pek Makanan)`
                    : `Pakej Percuma Ahli Keluarga (1 Calon = ${pricingConfig.freeSeatsPerPax} Kerusi + ${pricingConfig.freeMealsPerPax} Pek Makanan Percuma)`
                  }
                </span>
              </div>
              <span className="font-black text-emerald-700 uppercase tracking-wider text-[11px] sm:text-xs shrink-0 sm:text-right">PERCUMA (RM 0)</span>
            </div>

            {/* Extra Seats Add-on */}
            {extraSeats > 0 && (
              <div className="flex justify-between items-center py-2 border-b border-slate-100 text-xs sm:text-sm gap-2">
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-slate-900 block truncate">{extraSeats}x Tempat Duduk Tambahan Dewan</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs font-medium">(@ RM {currentSeatPrice}/kerusi)</span>
                </div>
                <span className="font-mono font-bold text-slate-900 shrink-0">RM {totalExtraSeatCost}.00</span>
              </div>
            )}

            {/* Extra Meals Add-on */}
            {extraMeals > 0 && (
              <div className="flex justify-between items-center py-2 border-b border-slate-100 text-xs sm:text-sm gap-2">
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-slate-900 block truncate">{extraMeals}x Pek Makanan Katering Tambahan</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs font-medium">(@ RM {currentMealPrice}/pax)</span>
                </div>
                <span className="font-mono font-bold text-slate-900 shrink-0">RM {totalExtraMealCost}.00</span>
              </div>
            )}

            {/* Promo Savings Banner */}
            {isPromoActive && totalFamilySavings > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between py-2 px-3 bg-amber-50 rounded-xl text-xs text-amber-950 font-bold border border-amber-200 gap-1">
                <span>Penjimatan Promosi Awalan Keluarga:</span>
                <span className="text-amber-800 font-black">- RM {totalFamilySavings}.00 (Jimat!)</span>
              </div>
            )}
          </div>

          {/* Grand Total Callout Box */}
          <div className="p-4 sm:p-5 bg-slate-900 text-white rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-6 text-center sm:text-left">
            <div>
              <span className="text-[11px] sm:text-xs text-slate-400 font-bold uppercase tracking-wider block">
                Jumlah Keseluruhan Perlu Dibayar
              </span>
              <span className="text-[11px] sm:text-xs text-slate-300">
                Merangkumi yuran ujian {totalCandidates > 0 ? `${totalCandidates} calon` : 'calon'} + {extraSeats} kerusi tambahan + {extraMeals} makanan tambahan
              </span>
            </div>
            <div className="sm:text-right">
              <span className="text-3xl sm:text-4xl font-black text-white block">
                RM {grandTotalWholeFamily}.00
              </span>

            </div>
          </div>

          {/* PAYMENT METHOD SELECTION (FPX vs QR) */}
          <div className="border-t border-slate-200 pt-5 sm:pt-6">
            {(showBothPaymentMethods || paymentConfig.paymentMethod === 'both') && (
              <div className="mb-5">
                <span className="text-xs font-bold text-slate-700 block mb-2">
                  Pilih Kaedah Pembayaran Pilihan Anda:
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPayMode('fpx')}
                    className={`py-3 px-3.5 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 cursor-pointer font-bold text-xs sm:text-sm ${selectedPayMode === 'fpx'
                      ? 'border-red-600 bg-red-50 text-red-700 shadow-xs'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                  >
                    <CreditCard className="w-4 h-4 text-red-600 shrink-0" />
                    <span>Perbankan FPX (ToyyibPay)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedPayMode('qr')}
                    className={`py-3 px-3.5 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 cursor-pointer font-bold text-xs sm:text-sm ${selectedPayMode === 'qr'
                      ? 'border-amber-500 bg-amber-50 text-amber-900 shadow-xs'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                  >
                    <QrCode className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>DuitNow QR Pay & Pindahan</span>
                  </button>
                </div>
              </div>
            )}

            {/* OPTION A: FPX ONLINE PAYMENT */}
            {selectedPayMode === 'fpx' && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-600 leading-relaxed flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-slate-900 font-bold mb-0.5">
                      Pembayaran Dalam Talian FPX Selamat (ToyyibPay)
                    </strong>
                    Invois rasmi ASMM akan dijana secara automatik. Anda boleh membayar terus menggunakan Maybank2u, CIMB Clicks, {persatuanSettings.bankName || 'Bank Islam'}, RHB, atau bank pilihan anda dengan pengesahan resit masa nyata.
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleCreateFamilyInvoiceAndPay}
                    disabled={isProcessingPayment}
                    className="py-4 px-6 bg-red-600 hover:bg-red-700 text-white font-extrabold text-sm sm:text-base rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-50"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>{isProcessingPayment ? 'Menjana Invois...' : `Bayar Sekarang RM ${grandTotalWholeFamily} (FPX)`}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSendWhatsApp(false)}
                    className="py-4 px-6 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-sm sm:text-base rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2.5 cursor-pointer"
                  >
                    <MessageCircle className="w-5 h-5 text-emerald-400" />
                    <span>Hantar Butiran ke WhatsApp Gelanggang</span>
                  </button>
                </div>
              </div>
            )}

            {/* OPTION B: DUITNOW QR PAY & BANK TRANSFER */}
            {selectedPayMode === 'qr' && (
              <div className="space-y-4">
                {/* 1. Official Bank & DuitNow QR Card */}
                <div className="p-4 sm:p-5 bg-amber-50/70 rounded-2xl sm:rounded-3xl border-2 border-amber-200 space-y-4">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 sm:gap-5">
                    <div className="space-y-2 text-center sm:text-left flex-1 min-w-0">
                      <div className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-amber-200 text-amber-900 text-xs font-black uppercase tracking-wider">
                          <QrCode className="w-3.5 h-3.5" />
                          <span>DuitNow QR Persatuan</span>
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider">
                          Akaun Rasmi Induk
                        </span>
                      </div>

                      <h3 className="text-base sm:text-lg font-black text-slate-900">
                        {persatuanSettings.bankName || 'Bank Islam Malaysia Berhad'}
                      </h3>

                      <p className="text-xs text-slate-600 font-medium leading-snug">
                        Pemegang Akaun: <strong className="text-slate-900">{persatuanSettings.accountHolder || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH'}</strong>
                      </p>

                      <div className="flex items-center justify-center sm:justify-start gap-2 pt-1 flex-wrap">
                        <span className="font-mono text-sm sm:text-lg font-black text-slate-900 bg-white px-3 py-1 rounded-xl border border-amber-300 shadow-2xs break-all">
                          {persatuanSettings.accountNo || '02011010087654'}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyAccountNo}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1 shadow-2xs active:scale-95"
                        >
                          {isAccountCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{isAccountCopied ? 'Disalin' : 'Salin'}</span>
                        </button>
                      </div>

                      <div className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 pt-1 text-[11px] text-amber-900 flex-wrap">
                        <span>Rujukan Bayaran: <strong className="font-mono text-xs sm:text-sm font-black text-slate-900 bg-white px-2.5 py-1 rounded-xl border border-amber-300 shadow-2xs tracking-wide">{candidateRefText}</strong></span>
                        <button
                          type="button"
                          onClick={handleCopyRefText}
                          className="px-2.5 py-1 bg-white hover:bg-amber-100 border border-amber-300 text-amber-900 text-[10px] font-bold rounded-lg transition cursor-pointer flex items-center gap-1 active:scale-95 shadow-2xs"
                          title="Salin Rujukan Bayaran"
                        >
                          {isRefNotesCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-amber-700" />}
                          <span>{isRefNotesCopied ? 'Disalin' : 'Salin'}</span>
                        </button>

                      </div>

                      <p className="text-[11px] text-amber-900/90 font-medium leading-relaxed pt-0.5">
                        {persatuanSettings.paymentInstructions || `Sila masukkan rujukan "${candidateRefText}" semasa pindahan DuitNow atau perbankan dalam talian.`}
                      </p>
                    </div>

                    {/* QR Code Container */}
                    <div className="w-36 sm:w-44 bg-white p-2.5 rounded-2xl border-2 border-amber-300 shadow-sm flex flex-col items-center justify-center shrink-0 mx-auto sm:mx-0">
                      {persatuanSettings.qrImageUrl && persatuanSettings.qrImageUrl.trim().startsWith('<svg') ? (
                        <div
                          className="w-28 h-28 sm:w-32 sm:h-32 aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
                          dangerouslySetInnerHTML={{ __html: persatuanSettings.qrImageUrl }}
                        />
                      ) : (
                        <img
                          src={persatuanSettings.qrImageUrl || '/badges/qr-persatuan-asmm.png'}
                          alt={`DuitNow QR ${persatuanSettings.bankName}`}
                          className="w-28 h-28 sm:w-32 sm:h-32 object-contain rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src.indexOf('/badges/qr-persatuan-asmm.png') === -1) {
                              target.src = '/badges/qr-persatuan-asmm.png';
                            }
                          }}
                        />
                      )}
                      <span className="text-[10px] font-black text-slate-900 uppercase tracking-wider text-center mt-1.5">
                        PSSASMM
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Official Receipt Upload & Verification Form */}
                {isReceiptSubmitted && submittedInvoice ? (
                  /* Success Confirmation State */
                  <div className="p-5 sm:p-6 bg-emerald-50/90 border-2 border-emerald-300 rounded-2xl sm:rounded-3xl text-center space-y-4 shadow-sm animate-in fade-in duration-300">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto ring-4 ring-emerald-200/60 shadow-xs">
                      <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <span className="inline-block px-3 py-1 rounded-full bg-emerald-200/80 text-emerald-900 text-[10px] sm:text-xs font-black uppercase tracking-wider mb-1.5">
                        Resit Bayaran Diterima
                      </span>
                      <h3 className="text-base sm:text-xl font-black text-slate-900">
                        Pendaftaran Ujian Bengkung Berjaya Dihantar!
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto mt-1 leading-relaxed">
                        Fail resit telah disimpan dalam arkib rasmi Google Drive JSON Ahli. Status pendaftaran kini <strong>Menunggu Pengesahan</strong> bendahari ASMM.
                      </p>
                    </div>

                    {/* Verification Details Box */}
                    <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-emerald-200/80 text-left text-xs space-y-2 max-w-lg mx-auto">
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">No. Invois:</span>
                        <span className="font-mono font-black text-slate-900">{submittedInvoice.invoiceNo}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">No. Rujukan:</span>
                        <span className="font-mono font-black text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200">
                          {submittedInvoice.transactionRef || receiptRefId || candidateRefText}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">Jumlah Bayaran:</span>
                        <span className="font-mono font-black text-slate-900 text-sm">RM {submittedInvoice.amount}.00</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-slate-100">
                        <span className="text-slate-500 font-medium">Tarikh & Masa:</span>
                        <span className="font-mono font-bold text-slate-700">{submittedInvoice.paidAt || `${paidDate} ${paidTime}`}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-slate-500 font-medium">Nama Fail Resit:</span>
                        <span className="font-mono text-[11px] text-slate-600 truncate max-w-[180px] sm:max-w-[240px]" title={submittedInvoice.receiptFileName}>
                          {submittedInvoice.receiptFileName || 'Slip_Resit_Bayaran'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 pt-2 max-w-lg mx-auto">
                      <button
                        type="button"
                        onClick={handleSendSubmittedReceiptWhatsApp}
                        className="py-3 sm:py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Hantar ke WhatsApp Jurulatih</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsReceiptSubmitted(false);
                          setUploadedReceiptFile(null);
                          setReceiptFilePreview(null);
                        }}
                        className="py-3 sm:py-3.5 px-4 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 font-bold text-xs sm:text-sm rounded-2xl transition flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <RefreshCw className="w-4 h-4 text-slate-500" />
                        <span>Muat Naik Resit Baharu</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Standard Upload Card */
                  <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-2xs space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div className="flex items-center gap-2.5">
                        <span className="p-2 bg-red-50 text-red-600 rounded-xl border border-red-100 shrink-0">
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        </span>
                        <div>
                          <h4 className="text-xs sm:text-base font-black text-slate-900">
                            Muat Naik Resit Bayaran (PDF / Imej)
                          </h4>
                          <p className="text-[10px] sm:text-[11px] text-slate-500">
                            Sila muat naik slip transaksi (.pdf, .png, .jpg, .jpeg, .webp maks 10MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Dropzone */}
                    <label className={`flex flex-col items-center justify-center p-4 sm:p-6 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${uploadedReceiptFile
                      ? 'border-emerald-500 bg-emerald-50/20'
                      : 'border-slate-300 hover:border-red-500 hover:bg-red-50/10 bg-slate-50/50'
                      }`}>
                      <div className="p-2.5 sm:p-3 bg-red-50 text-red-600 rounded-2xl mb-2">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-xs font-bold text-slate-800 text-center max-w-xs break-all">
                        {uploadedReceiptFile ? uploadedReceiptFile.name : 'Pilih atau Tarik Fail Resit (PDF / Imej)'}
                      </span>
                      <span className="text-[10px] text-slate-400 mt-1 text-center">
                        Format fail: Dokumen PDF (.pdf) atau fail imej (.png, .jpg, .jpeg, .webp). Maksimum 10MB.
                      </span>
                      <input
                        type="file"
                        accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp"
                        onChange={handleReceiptFileChange}
                        className="hidden"
                      />
                    </label>

                    {/* Receipt Image Preview */}
                    {receiptFilePreview && (
                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 text-center">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Pratonton Resit Bayaran</span>
                        <img
                          src={receiptFilePreview}
                          alt="Pratonton Resit"
                          className="max-h-48 max-w-full mx-auto rounded-xl object-contain border border-slate-200 shadow-2xs"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Scanning PDF / Image Progress Banner */}
                    {isScanningPdf && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-2.5 animate-pulse">
                        <Loader2 className="w-4 h-4 text-amber-600 animate-spin shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-xs font-black text-amber-950 flex items-center gap-1.5">
                            <ScanText className="w-3.5 h-3.5 text-amber-700" />
                            <span>Mengimbas Butiran Resit Secara Pintar...</span>
                          </p>
                          <p className="text-[11px] text-amber-800 truncate font-medium">
                            {scanProgress || 'Mencari nombor rujukan & tarikh transaksi...'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* File Details & Audit Form Fields */}
                    {uploadedReceiptFile && (
                      <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="p-1.5 bg-emerald-100 text-emerald-800 rounded-lg shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 text-left">
                              <span className="text-xs font-bold text-slate-900 truncate block">
                                {uploadedReceiptFile.name}
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                {(uploadedReceiptFile.size / 1024).toFixed(1)} KB • {uploadedReceiptFile.type.includes('pdf') ? 'Dokumen PDF' : 'Fail Imej'}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedReceiptFile(null);
                              setReceiptFilePreview(null);
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition cursor-pointer"
                            title="Padam Fail"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Audit Inputs */}
                        <div className="pt-2 border-t border-slate-200/80 space-y-2.5 text-left">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                              No. Rujukan Transaksi (Reference ID):
                            </label>
                            <input
                              type="text"
                              value={receiptRefId}
                              onChange={(e) => setReceiptRefId(e.target.value)}
                              placeholder={`cth: ${candidateRefText} / 045453508Q`}
                              className="w-full px-3 py-2 text-xs font-mono font-black text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none"
                            />
                            <span className="text-[9px] text-slate-400">
                              Disahkan oleh bendahari ASMM berdasarkan rekod pindahan bank.
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                                Tarikh Pembayaran:
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                                  <Calendar className="w-3.5 h-3.5" />
                                </div>
                                <input
                                  type="date"
                                  value={paidDate}
                                  onChange={(e) => setPaidDate(e.target.value)}
                                  className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none cursor-pointer"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                                Masa Pembayaran:
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                                  <Clock className="w-3.5 h-3.5" />
                                </div>
                                <input
                                  type="time"
                                  step="1"
                                  value={paidTime}
                                  onChange={(e) => setPaidTime(e.target.value)}
                                  className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none cursor-pointer"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Primary Upload Action Button */}
                    <button
                      type="button"
                      onClick={handleUploadAndSubmitReceipt}
                      disabled={isUploadingReceipt || !uploadedReceiptFile}
                      className="w-full py-3.5 sm:py-4 px-6 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-black text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed active:scale-98"
                    >
                      {isUploadingReceipt ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Memuat Naik Resit ke Google Drive...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Muat Naik & Hantar Pengesahan Resit (RM {grandTotalWholeFamily}.00)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Secondary Action: Switch to FPX if enabled */}
                {(showBothPaymentMethods || paymentConfig.paymentMethod === 'both') && (
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={handleCreateFamilyInvoiceAndPay}
                      disabled={isProcessingPayment}
                      className="w-full py-3.5 sm:py-4 px-5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      <CreditCard className="w-4 h-4 text-amber-400" />
                      <span>Tukar ke Bayaran FPX (ToyyibPay)</span>
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* 8. SECTION: CONTACT PENGURUSAN GELANGGANG */}
        <div className="mb-10">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              <span>Pengurusan Gelanggang Untuk Pengesahan Segera</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Sila hubungi terus pihak pengurusan gelanggang untuk pertanyaan susun atur dewan atau pengesahan kehadiran
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {/* Gelanggang Pusaka Warisan */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs hover:border-red-300 transition flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3">
                  <Building className="w-5 h-5" />
                </div>
                <h3 className="font-extrabold text-slate-900 text-sm">Pusaka Warisan</h3>
                <p className="text-xs font-bold text-slate-800 mt-1">Jurulatih Fikriz Zaki Syeikh Ahmad Amirruddin</p>
                <p className="text-[11px] text-slate-500">Ketua Gelanggang Pusaka Warisan</p>
                <div className="mt-2 text-xs font-mono font-bold text-slate-800">
                  013-581 9201
                </div>
              </div>
              <a
                href="https://wa.me/60135819201?text=Assalamualaikum%20Jurulatih%20Fikriz%20Zaki%2C%20saya%20ingin%20membuat%20pengesahan%20Ujian%20Bengkung%20dan%20kemudahan%20keluarga."
                target="_blank"
                rel="noreferrer"
                className="mt-4 w-full py-2.5 bg-slate-50 hover:bg-red-50 text-red-600 font-bold text-xs rounded-xl border border-slate-200 hover:border-red-200 transition text-center flex items-center justify-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp Jurulatih Pusaka Warisan</span>
              </a>
            </div>

            {/* Gelanggang Wadi AnNour */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs hover:border-blue-300 transition flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                  <Building className="w-5 h-5" />
                </div>
                <h3 className="font-extrabold text-slate-900 text-sm">Wadi AnNour</h3>
                <p className="text-xs font-bold text-slate-800 mt-1">Jurulatih Mohd Fauzi Bin Abd Aziz</p>
                <p className="text-[11px] text-slate-500">Ketua Gelanggang Wadi AnNour / Setiausaha Induk</p>
                <div className="mt-2 text-xs font-mono font-bold text-slate-800">
                  012-447 5157
                </div>
              </div>
              <a
                href="https://wa.me/60124475157?text=Assalamualaikum%20Jurulatih%20Mohd%20Fauzi%2C%20saya%20ingin%20membuat%20pengesahan%20Ujian%20Bengkung%20dan%20kemudahan%20keluarga."
                target="_blank"
                rel="noreferrer"
                className="mt-4 w-full py-2.5 bg-slate-50 hover:bg-blue-50 text-blue-600 font-bold text-xs rounded-xl border border-slate-200 hover:border-blue-200 transition text-center flex items-center justify-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp Jurulatih Wadi AnNour</span>
              </a>
            </div>
          </div>
        </div>

        {/* 9. FAQ & SOALAN LAZIM */}
        <div className="p-6 sm:p-8 rounded-3xl bg-slate-50 border border-slate-200 text-left">
          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-slate-600" />
            <span>Soalan Lazim & Panduan Kehadiran Dewan</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Jika saya mendaftar 2 orang anak, berapa kuota percuma kami?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Setiap calon membawa kuota 2 kerusi dan 2 pek makanan percuma. Jadi untuk 2 orang anak, keluarga anda secara automatik menikmati <strong>4 tempat duduk percuma</strong> dan <strong>4 pek makanan percuma</strong>!
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bila tarikh akhir promosi dan bagaimana jika terlepas?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Promosi awalan sah sehingga <strong>30 November 2026</strong>. Selepas tarikh ini, tempahan masih boleh dibuat tetapi tertakluk kepada harga asal (Kerusi: RM 37 & Makanan: RM 18) serta kekosongan kapasiti dewan.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bagaimanakah kaedah pembayaran yuran ujian bengkung dan tempahan keluarga dibuat?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Pembayaran dibuat secara terus menggunakan <strong>DuitNow QR Pay & Pindahan Akaun {persatuanSettings.bankName || 'Bank Islam Malaysia Berhad'} ASMM</strong>. Anda hanya perlu imbas kod QR atau salin nombor akaun rasmi yang disediakan, kemudian hantar resit transaksi melalui butang WhatsApp untuk pengesahan pendaftaran rasmi.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Apakah rujukan yang perlu diletakkan semasa membuat pindahan DuitNow QR?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Sila masukkan rujukan rasmi seperti <strong>"{candidateRefText}"</strong> (gabungan kod UB2026 bersama nombor ID ahli pesilat) pada resit pindahan perbankan dalam talian anda bagi memudahkan pihak bendahari ASMM menyemak dan mengesahkan rekod bayaran secara automatik.
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200/80">
              <strong className="block text-slate-900 text-sm mb-1">
                Bagaimana makanan diedarkan semasa majlis?
              </strong>
              <p className="text-slate-600 leading-relaxed">
                Pek katering berbungkus berkualiti akan diedarkan di kaunter pendaftaran dewan mengikut nama waris dan kuota yang telah disahkan.
              </p>
            </div>
          </div>
        </div>

        {/* Closing Footnote */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p className="font-semibold text-slate-700">Sekian, terima kasih.</p>
          <p className="mt-1">Jawatankuasa Pengurusan Ujian Bengkung • Persatuan Seni Silat Awang Sulung Merah Muda</p>
        </div>

      </div>
    </div>
  );
};

export default UjianBengkungPage;
